import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { getLiveOfficers, listActiveTracks } from "@/lib/tracking.functions";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PatrolMap } from "@/components/PatrolMap";
import { FieldPresenceStrip } from "@/components/dashboards/FieldPresenceStrip";
import { OnDutyRoster } from "@/components/dashboards/OnDutyRoster";
import { useFieldPresence } from "@/lib/dashboard/use-field-presence";

export const Route = createFileRoute("/_authenticated/tracking/")({ component: TrackingPage });

function TrackingPage() {
  const { currentOrg } = useAuth();
  const presence = useFieldPresence(30_000);
  const fetchLive = useServerFn(getLiveOfficers);
  const fetchTracks = useServerFn(listActiveTracks);
  const orgId = currentOrg?.id;
  const [showGeofences, setShowGeofences] = useState(true);

  const live = useQuery({
    queryKey: ["tracking", "live", orgId],
    queryFn: () => fetchLive({ data: { orgId: orgId! } }),
    enabled: !!orgId,
    refetchInterval: 8000,
  });
  const active = useQuery({
    queryKey: ["tracking", "active", orgId],
    queryFn: () => fetchTracks({ data: { orgId: orgId! } }),
    enabled: !!orgId,
    refetchInterval: 15000,
  });
  const geofencesQ = useQuery({
    queryKey: ["tracking", "geofences", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("geofences")
        .select("id,name,kind,center_lat,center_lng,radius_m,active")
        .eq("organization_id", orgId!);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!orgId,
  });

  const pings = (live.data ?? []).filter((r: any) => r.latitude && r.longitude)
    .map((r: any) => ({ latitude: Number(r.latitude), longitude: Number(r.longitude), recorded_at: r.recorded_at }));
  const allFences = (geofencesQ.data ?? []) as any[];
  const enabledFences = allFences.filter((g: any) => g.active);
  const geofences = showGeofences
    ? enabledFences.map((g: any) => ({
        id: g.id, name: g.name, kind: g.kind,
        center_lat: Number(g.center_lat), center_lng: Number(g.center_lng), radius_m: Number(g.radius_m),
      }))
    : [];
  // Counts describe the configured geofences themselves — the ON/OFF switch only
  // controls whether the layer is drawn, never how many geofences exist.
  const configuredCount = allFences.length;
  const enabledCount = enabledFences.length;
  const restrictedCount = enabledFences.filter((g: any) => g.kind === "exclusion").length;

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Command View</h1>
          <p className="text-sm text-muted-foreground">Live officer positions, stale detection, and active shifts.</p>
        </div>
        <div className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2 text-sm">
          <Switch id="show-geofences" checked={showGeofences} onCheckedChange={setShowGeofences} />
          <Label htmlFor="show-geofences" className="cursor-pointer">Geofences</Label>
          <span className="inline-flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            {configuredCount} configured
            <span className="ml-1 inline-block h-2 w-2 rounded-full" style={{ background: "#22d3ee" }} /> {enabledCount} enabled
            <span className="ml-1 inline-block h-2 w-2 rounded-full" style={{ background: "#ef4444" }} /> {restrictedCount} restricted
          </span>
        </div>
      </div>
      <FieldPresenceStrip presence={presence} />
      <OnDutyRoster presence={presence} />
      <Card>
        <CardContent className="p-0">
          <PatrolMap pings={pings} geofences={geofences} height={420} fitTo="both" enableStyleToggle defaultStyle="dark" />
        </CardContent>
      </Card>
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Officers ({live.data?.length ?? 0})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(live.data ?? []).map((o: any) => (
              <div key={o.officer_id} className="flex items-center justify-between rounded-md border border-border p-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{o.profile?.full_name ?? o.profile?.email ?? o.officer_id.slice(0, 8)}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(o.recorded_at).toLocaleTimeString()} · battery {o.battery_pct ?? "—"}%
                  </div>
                </div>
                <Badge variant={o.stale ? "destructive" : "secondary"}>{o.stale ? "stale" : (o.status ?? "online")}</Badge>
              </div>
            ))}
            {(live.data ?? []).length === 0 && <div className="text-sm text-muted-foreground">No telemetry yet.</div>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Active shifts ({active.data?.length ?? 0})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(active.data ?? []).map((t: any) => (
              <Link key={t.id} to="/tracking/$shiftId" params={{ shiftId: t.shift_id }}
                    className="flex items-center justify-between rounded-md border border-border p-2 text-sm hover:bg-accent">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">Shift {t.shift_id.slice(0, 8)}</div>
                  <div className="text-xs text-muted-foreground">
                    {t.point_count} pts · {Math.round(Number(t.distance_m ?? 0))} m
                  </div>
                </div>
                <Badge variant="outline">replay</Badge>
              </Link>
            ))}
            {(active.data ?? []).length === 0 && <div className="text-sm text-muted-foreground">No active tracks.</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
